<template>
  <el-dialog
    :visible="!!file"
    @close="$emit('close')"
  >
    <ContentHeader
      slot="title"
      :title="title"
    />
    <el-alert
      v-if="failed"
      :title="$t('sampleProgress.files.revisionsError')"
      type="error"
      show-icon
      :closable="false"
    >
      <el-button
        type="text"
        @click="load"
      >
        {{ $t('sampleProgress.retry') }}
      </el-button>
    </el-alert>
    <template v-else>
      <el-alert
        :title="$t('sampleProgress.files.onlyCurrentDownloadable')"
        type="info"
        show-icon
        :closable="false"
      />
      <el-table
        v-loading="loading"
        :data="revisions"
      >
        <el-table-column :label="$t('sampleProgress.files.revision')">
          <template #default="{ row }">
            {{ $t('sampleProgress.files.revisionNumber', { n: row.revision }) }}
            <el-tag
              v-if="isCurrent(row)"
              size="mini"
            >
              {{ $t('sampleProgress.files.current') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('sampleProgress.files.name')">
          <template #default="{ row }">
            <ProgressFileName :file="row" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('sampleProgress.files.date')"
          :formatter="formatDate"
        />
        <el-table-column align="right">
          <template #default="{ row }">
            <ProgressFileDownload
              v-if="isCurrent(row)"
              :sample-id="sampleId"
              :file="row"
            />
          </template>
        </el-table-column>
      </el-table>
    </template>
  </el-dialog>
</template>

<script>
import { sampleProgressApi } from '@/api';
import ContentHeader from '@/components/ContentHeader.vue';
import ProgressFileDownload from '@/components/ProgressFileDownload.vue';
import ProgressFileName from '@/components/ProgressFileName.vue';
import progressFile from '@/services/progressFile';

/**
 * The whole history of one chain, current revision first. Earlier revisions are listed for
 * the record only: just the current one is downloadable. Open while it is given a file box row.
 */
export default {
  name: 'ProgressFileRevisions',
  components: {
    ContentHeader,
    ProgressFileDownload,
    ProgressFileName,
  },
  props: {
    sampleId: {
      type: Number,
      required: true,
    },
    // The file box row of the chain.
    file: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // Kept past closing, so the dialog does not empty while it fades out.
      chain: {},
      revisions: [],
      loading: false,
      failed: false,
    };
  },
  computed: {
    title() {
      return this.$t('sampleProgress.files.revisionsTitle', { name: this.chain.displayName });
    },
  },
  watch: {
    file: {
      immediate: true,
      handler(file) {
        if (file) this.open(file);
      },
    },
  },
  methods: {
    open(file) {
      this.chain = file;
      this.revisions = [];
      this.load();
    },
    async load() {
      const { chainId } = this.chain;
      this.loading = true;
      this.failed = false;
      try {
        const { data } = await sampleProgressApi.findChainRevisions(this.sampleId, chainId);
        // Reopened on another chain meanwhile: its own answer is the one that counts.
        if (chainId !== this.chain.chainId) return;
        this.revisions = data;
      } catch (error) {
        if (chainId !== this.chain.chainId) return;
        this.failed = true;
      } finally {
        if (chainId === this.chain.chainId) this.loading = false;
      }
    },
    isCurrent({ attachmentId }) {
      return attachmentId === this.chain.attachmentId;
    },
    formatDate(row) {
      return progressFile.date(row);
    },
  },
};
</script>
